// ===== 路由与视图类型 =====

export type RouteName = "home" | "login" | "register" | "verifyEmail" | "app" | "admin" | "chat" | "alipayReturn";
export type AppTab = "overview" | "keys" | "entitlements" | "wallet" | "orders" | "usage" | "modelUsage" | "settings" | "docs";
export type AuthTab = "login" | "register";
export type AdminView = "team" | "users" | "userDetail" | "channels" | "wallets" | "products" | "orders" | "entitlements" | "gpt" | "health" | "docs";
export type AdminUserDetailTab = "usage" | "policy-limits" | "keys";
export type AdminKeyDrawerTab = "overview" | "limits" | "policy";

export interface AdminKeyDrawerState {
  id: string;
  tab: AdminKeyDrawerTab;
}

// ===== 数据模型 =====

export interface UsageSummary {
  requests: number;
  errors: number;
  cancelled?: number;
  inputChars?: number;
  outputChars?: number;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
}

export interface ModelUsageItem {
  model: string;
  requests: number;
  totalTokens: number;
}

export interface ModelUsageSummary {
  requests: number;
  totalTokens: number;
  models: ModelUsageItem[];
}

export interface ModelUsagePeriods {
  day: ModelUsageSummary;
  week: ModelUsageSummary;
  month: ModelUsageSummary;
  total: ModelUsageSummary;
}

export interface ModelUsageKey {
  id: string;
  name: string;
  keyPrefix: string;
  periods: ModelUsagePeriods;
}

export interface AppModelUsage {
  periods: ModelUsagePeriods;
  apiKeys: ModelUsageKey[];
}

export interface CompressionSummary {
  requests: number;
  applied: number;
  errors: number;
  inputTokensBefore: number;
  inputTokensAfter: number;
  estimatedSavedTokens: number;
}

export interface CompressionRiskSummary {
  requests: number;
  applied: number;
  failed: number;
  compressionErrors: number;
}

export interface ApiKey {
  id: string;
  name: string;
  keyPrefix: string;
  keyHash: string;
  status: string;
  lastUsedAt: string | null;
  createdAt: string;
  memberId: string;
  channelId?: string | null;
  /** 明文 Key,仅在创建时返回 */
  plainKey?: string;
  /** 今日用量(管理端按 Key 聚合) */
  todayUsage?: UsageSummary;
  /** 本周用量(Asia/Shanghai 自周一 00:00 起) */
  weekUsage?: UsageSummary;
  /** 本月用量(Asia/Shanghai 自自然月 1 日 00:00 起) */
  monthUsage?: UsageSummary;
  /** 累计用量(管理端按 Key 聚合) */
  totalUsage?: UsageSummary;
  modelUsage?: ModelUsagePeriods;
  compressionToday?: CompressionSummary;
  compressionTotal?: CompressionSummary;
  compressionRisk?: CompressionRiskSummary;
  compressionPolicy?: string | null;
  compressionOptimizationPolicy?: string | null;
  interactionCaptureEnabled?: boolean | null;
  usageLimit?: UsageLimitSummary | null;
  accountUsageLimit?: UsageLimitSummary | null;
}

export interface UsageLimitConfig {
  dailyCreditLimit: number | null;
  monthlyCreditLimit: number | null;
  maxConcurrentRequests: number | null;
  updatedAt?: string | null;
}

export interface UsageLimitSummary {
  limits: UsageLimitConfig;
  /** Administrator-owned hard cap for an API Key. */
  adminLimits?: UsageLimitConfig;
  /** API Key owner's self-managed sub-limit. */
  userLimits?: UsageLimitConfig;
  /** Strictest field-by-field result of administrator and owner limits. */
  effectiveLimits?: UsageLimitConfig;
  day: { settledCredits: number; reservedCredits: number };
  month: { settledCredits: number; reservedCredits: number };
  currentConcurrency: number;
  periodStarts?: { day: string; month: string };
}

export interface Member {
  id: string;
  name: string;
  email: string;
  status: string;
  plan: string;
  /** JSON 字符串,如 '["admin","customer"]' */
  roles: string;
  quotaPerDay: number | null;
  rateLimitPerMinute: number | null;
  trialEndsAt: string | null;
  defaultChannelId?: string | null;
  usageToday: UsageSummary;
  usageTotal?: UsageSummary;
  compressionToday?: CompressionSummary;
  compressionTotal?: CompressionSummary;
  compressionRisk?: CompressionRiskSummary;
  compressionPolicy?: string | null;
  compressionOptimizationPolicy?: string | null;
  interactionCaptureEnabled?: boolean;
  apiKeys: ApiKey[];
  deletedAt: string | null;
}

export interface RequestLog {
  id: string;
  requestId?: string;
  memberId: string;
  memberName?: string | null;
  memberEmail?: string | null;
  apiKeyId: string;
  apiKeyName?: string | null;
  channelId?: string | null;
  channelName?: string | null;
  model: string;
  status: string;
  latencyMs: number;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  rawCachedInputTokens?: number;
  rawReasoningTokens?: number;
  billableInputTokens?: number;
  billedTokens?: number | null;
  billedCost?: number | null;
  billingCurrency?: string | null;
  pricingUnit?: string | null;
  pricingSource?: string | null;
  pricingVersion?: string | null;
  usageEventStatus?: string | null;
  error: boolean;
  errorCode?: string | null;
  errorMessage: string;
  conversationId: string;
  createdAt: string;
  providerId?: string;
}

export interface HealthLayer {
  name: string;
  ok: boolean;
  latencyMs: number | null;
  error: string | null;
  url: string;
  expiresAt?: string | null;
  expiresIn?: number | null;
}

export interface HealthStatus {
  timestamp: string;
  layers: HealthLayer[];
}

export interface OauthStatus {
  authPath: string;
  exists: boolean;
  hasAccessToken: boolean;
  hasRefreshToken: boolean;
  tokenType: string;
  accountId: string;
  expiresAt: string | null;
  expiresIn: number | null;
  expired: boolean;
  error: string | null;
}

// ===== 聊天 =====

export interface ChatSource {
  title?: string | null;
  url: string;
}

export interface ChatAttachment {
  name: string;
  mimeType: string;
  size: number;
  dataUrl: string;
}

export interface ChatProcessStep {
  key: string;
  label: string;
  status: "active" | "done";
}

export interface ChatSession {
  id: string;
  conversationId: string;
  memberId: string;
  channelId?: string | null;
  bridgeInstanceId?: string | null;
  title: string;
  lastMessageRole?: "user" | "assistant" | null;
  lastMessageContent?: string | null;
  lastMessageAt?: string | null;
  messageCount?: number;
  createdAt: string;
  updatedAt: string;
}

export interface ChatMessage {
  id: string;
  sessionId: string;
  role: "user" | "assistant";
  content: string;
  sources?: ChatSource[];
  attachments?: ChatAttachment[];
  createdAt: string;
  usage?: {
    inputTokens: number;
    outputTokens: number;
    totalTokens: number;
    cachedInputTokens?: number;
    reasoningTokens?: number;
  } | null;
}

export interface ChatQuota {
  used: number;
  limit: number | null;
  remaining: number | null;
  periodStart: string;
  freeChat?: {
    total: number;
    consumed: number;
    reserved: number;
    remaining: number;
  } | null;
}

export interface ChatModelOption {
  modelName: string;
  label: string;
  isDefault: boolean;
}

/** SeekToken 公共模型目录 */
export interface PublicModel {
  id: string;
  modelName: string;
  upstreamModel: string;
  apiBase: string;
  dbModel: boolean;
  modelKind: "public" | "internal_route";
  isInternalRoute: boolean;
  publicModelName: string | null;
  createdAt: string | null;
  catalogSource?: "admin" | "imported";
  isDeletable?: boolean;
}

export interface PublicModelManagementStatus {
  dynamicEnabled: boolean;
  databaseUrlConfigured: boolean;
  requiresPrisma: boolean;
  catalogBacked?: boolean;
  execution?: "channel_direct";
}

/** ChatGPT 账号池中的账号 token 状态 */
export interface AccountTokenStatus {
  exists: boolean;
  hasAccessToken: boolean;
  hasRefreshToken: boolean;
  tokenType?: string;
  accountId?: string | null;
  expiresAt?: string | null;
  expiresIn?: number | null;
  expired?: boolean | null;
  error?: string | null;
}

/** ChatGPT 账号池中的账号 */
export interface ChatGptAccount {
  id: string;
  label: string;
  createdAt: string;
  updatedAt: string;
  status: AccountTokenStatus;
}

/** ChatGPT 账号自动切换事件 */
export interface AccountFailoverEvent {
  id: string;
  requestId?: string | null;
  route: string;
  trigger: string;
  fromAccountId?: string | null;
  fromAccountLabel?: string | null;
  toAccountId?: string | null;
  toAccountLabel?: string | null;
  status: string;
  errorStatus?: number | null;
  errorCode?: string | null;
  errorMessage?: string | null;
  reloadStatus?: string | null;
  retryStatus?: string | null;
  message?: string | null;
  createdAt: string;
  updatedAt?: string | null;
}

/** 用户 Token 钱包 */
export interface TokenWallet {
  memberId: string;
  balance: number;
  balancePoints?: number;
  reservedPoints?: number;
  availablePoints?: number;
  totalPurchased: number;
  totalPurchasedPoints?: number;
  totalConsumed: number;
  totalConsumedPoints?: number;
  createdAt: string;
  updatedAt: string;
}

/** 点数钱包流水 */
export interface WalletTransaction {
  id: string;
  memberId: string;
  type: "grant" | "consume" | string;
  amount: number;
  balanceAfter: number;
  requestId?: string | null;
  usageEventId?: string | null;
  orderId?: string | null;
  model?: string | null;
  rawTokens?: number | null;
  billedTokens?: number | null;
  billedMicrocredits?: number | null;
  multiplier?: number | null;
  billedCost?: number | null;
  billingCurrency?: string | null;
  pricingUnit?: string | null;
  cashTransactionId?: string | null;
  cashAmount?: number | null;
  cashCurrency?: string | null;
  cashTransactionType?: string | null;
  note: string | null;
  operatorMemberId: string | null;
  createdAt: string;
  member?: User | null;
  operator?: User | null;
}

export interface WalletTransactionSummary {
  transactionCount: number;
  totalRechargeAmount: number;
  totalRechargePoints: number;
  totalConsumedPoints: number;
  currency: string;
}

export interface PaginationMetadata {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export interface AppDashboardSummary {
  tokenUsage: {
    total: UsageSummary;
    month: UsageSummary;
    week: UsageSummary;
    day: UsageSummary;
  };
  recharge: {
    totalAmount: number;
    monthAmount: number;
    currency: string;
  };
  billing: {
    totalMicrocredits: number;
  };
  points: {
    totalConsumed: number;
    monthConsumed: number;
  };
}

export interface MemberEntitlement {
  id: string;
  memberId: string;
  orderId: string;
  planId: string | null;
  type: "monthly_pass";
  status: "purchased" | "active" | "exhausted" | "expired" | "revoked" | string;
  totalPoints: number;
  consumedPoints: number;
  reservedPoints: number;
  availablePoints: number;
  purchasedAt: string;
  activateBefore: string | null;
  activatedAt: string | null;
  expiresAt: string | null;
  entitlementSnapshot: Record<string, unknown>;
  revokedAt: string | null;
  revokeReason: string | null;
  createdAt: string;
  updatedAt: string;
  member?: User | null;
  order?: Order | null;
}

export interface MemberEntitlementsSummary {
  activeEntitlement: MemberEntitlement | null;
  pendingEntitlements: MemberEntitlement[];
  history: MemberEntitlement[];
}

export interface BillingFundingSummary {
  monthlyPass: {
    active: boolean;
    id?: string | null;
    availablePoints: number;
    totalPoints?: number;
    consumedPoints?: number;
    reservedPoints?: number;
    expiresAt: string | null;
  };
  wallet: {
    balance: number;
    reservedPoints: number;
    availablePoints: number;
  };
  totalAvailablePoints: number;
  spendPriority: string[];
}

export interface EntitlementTransaction {
  id: string;
  entitlementId: string;
  memberId: string;
  usageEventId: string | null;
  requestId: string | null;
  type: string;
  amount: number;
  consumedAfter: number;
  note: string | null;
  createdAt: string;
}

export interface UsageFundingAllocation {
  id: string;
  requestId: string;
  usageEventId: string | null;
  memberId: string;
  sourceType: "monthly_pass" | "wallet" | string;
  sourceId: string | null;
  reservedPoints: number;
  settledPoints: number;
  releasedPoints: number;
  status: string;
  expiresAt: string;
  settledAt: string | null;
  releasedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface BillingFundingEvent {
  id: string;
  type: string;
  requestId: string | null;
  usageEventId: string | null;
  memberId: string | null;
  code: string | null;
  message: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
}

export interface MonthlyPassOperations {
  summary: {
    entitlements: {
      pendingCount: number;
      activeCount: number;
      expiredCount: number;
      exhaustedCount: number;
      revokedCount: number;
      pendingPoints: number;
      pendingAmount: number;
      activeAvailablePoints: number;
      expiredUnusedPoints: number;
      totalConsumedPoints: number;
      totalReservedPoints: number;
      currency: string;
    };
    funding: {
      reservedAllocationCount: number;
      expiredReservedAllocationCount: number;
      settledAllocationCount: number;
      releasedAllocationCount: number;
      reservedPoints: number;
      monthlyPassSettledPoints: number;
      walletSettledPoints: number;
      releasedPoints: number;
      mixedRequestCount: number;
      insufficientFundsCount: number;
      fundingShortfallCount: number;
      cleanupReleaseCount: number;
    };
  };
  entitlementTransactions: EntitlementTransaction[];
  fundingAllocations: UsageFundingAllocation[];
  fundingEvents: BillingFundingEvent[];
}

export interface RechargeRevenueSummary {
  today: number;
  week: number;
  month: number;
  total: number;
  currency: string;
}

/** 用户(Member 的公开字段子集) */
export interface User {
  id: string;
  name: string;
  email: string;
  status: string;
  plan: string;
  /** JSON 字符串 */
  roles: string;
  emailVerifiedAt?: string | null;
  emailVerificationSentAt?: string | null;
  defaultChannelId?: string | null;
  /** 最近一次成功请求时间，覆盖 Web Chat 与 API Key 请求 */
  lastActivityAt?: string | null;
  todayUsage?: UsageSummary;
  weekUsage?: UsageSummary;
  monthUsage?: UsageSummary;
  totalUsage?: UsageSummary;
  modelUsage?: ModelUsagePeriods;
  apiKeys?: ApiKey[];
  compressionToday?: CompressionSummary;
  compressionTotal?: CompressionSummary;
  compressionRisk?: CompressionRiskSummary;
  compressionPolicy?: string | null;
  compressionOptimizationPolicy?: string | null;
  interactionCaptureEnabled?: boolean;
  wallet?: TokenWallet | null;
  walletTransactions?: WalletTransaction[];
  usageLimit?: UsageLimitSummary | null;
}

// ===== API 错误 =====

export class ApiError extends Error {
  code?: string;
  details: Record<string, unknown> | undefined;
  status: number;
  constructor(message: string, status: number, code?: string, details?: Record<string, unknown>) {
    super(message);
    this.name = "ApiError";
    this.code = code;
    this.details = details;
    this.status = status;
  }
}

// ===== 应用状态类型 =====

export interface PaginationState {
  page: number;
  pageSize: number;
}

export interface WalletTransactionFilters {
  memberId: string;
  from: string;
  to: string;
  type: string;
}

export interface State {
  members: Member[];
  requestLogs: RequestLog[];
  requestLogsLoaded: boolean;
  requestLogsLoading: boolean;
  todayErrorRequests: RequestLog[];
  todayErrorRequestsOpen: boolean;
  todayErrorRequestsLoading: boolean;
  todayErrorRequestsPagination: PaginationMetadata;
  currentView: AdminView;
  pendingApiKeyMemberId: string;
  pendingApiKeyIsUser: boolean;
  expandedMemberIds: Set<string>;
  plainApiKeys: Record<string, string>;
  pagination: {
    members: PaginationState;
    requestLogs: PaginationState;
    users: PaginationState;
  };
  user: User | null;
  csrfToken: string;
  userApiKeys: ApiKey[];
  route: RouteName;
  appTab: AppTab;
  authTab: AuthTab;
  adminUsers: User[];
  adminInteractionCapturePolicy: InteractionCapturePolicyState | null;
  adminChannels: ProviderChannel[];
  adminChannelOverrides: ChannelOverride[];
  adminProviderInstances: ProviderInstance[];
  adminBridgeInstances: ProviderBridgeInstance[];
  adminBridgeFailoverEvents: BridgeFailoverEvent[];
  adminChannelTab: "channels" | "instances";
  adminUsersSearch: string;
  adminSelectedUserId: string | null;
  adminUserDetailTab: AdminUserDetailTab;
  adminModelUsagePeriod: keyof ModelUsagePeriods;
  adminKeyDrawer: AdminKeyDrawerState | null;
  adminWalletTransactions: WalletTransaction[];
  adminWalletTransactionSummary: WalletTransactionSummary | null;
  adminWalletTransactionsLoaded: boolean;
  adminWalletTransactionsLoading: boolean;
  adminWalletFilters: WalletTransactionFilters;
  adminRechargeRevenue: RechargeRevenueSummary | null;
  adminHealth: HealthStatus | null;
  adminOauth: OauthStatus | null;
  appDashboard: AppDashboardSummary | null;
  appModelUsage: AppModelUsage | null;
  appModelUsagePeriod: keyof ModelUsagePeriods;
  appUsage?: UsageSummary;
  appUsageRecords: RequestLog[];
  appUsagePagination: PaginationMetadata;
  appWallet: TokenWallet | null;
  appEntitlements: MemberEntitlementsSummary | null;
  appFundingSummary: BillingFundingSummary | null;
  appWalletTransactions: WalletTransaction[];
  appWalletFilters: WalletTransactionFilters;
  appWalletPagination: PaginationMetadata;
  appOrders: Order[];
  // 聊天
  chatSessions: ChatSession[];
  chatCurrentSessionId: string;
  chatMessages: ChatMessage[];
  chatQuota: ChatQuota | null;
  chatAvailableModels: ChatModelOption[];
  chatSelectedModel: string;
  chatStreaming: boolean;
  chatStreamingText: string;
  chatStreamingReasoning: string;
  chatStreamingStatus: string;
  chatStreamingSteps: ChatProcessStep[];
  chatStreamingSources: ChatSource[];
  chatPendingAttachments: ChatAttachment[];
  // 公共模型目录
  publicModels: PublicModel[];
  publicModelManagement: PublicModelManagementStatus | null;
  // ChatGPT 账号池
  chatGptAccounts: ChatGptAccount[];
  chatGptFailoverEvents: AccountFailoverEvent[];
  // 定价方案
  pricingPlans: PricingPlan[];
  billingModelRates: BillingModelRates | null;
  // 订单
  adminOrders: Order[];
  adminEntitlements: MemberEntitlement[];
  adminMonthlyPassOperations: MonthlyPassOperations | null;
}

export interface InteractionCapturePolicyState {
  enabled: boolean;
  runtimeEnabled: boolean;
}

export interface TokenPeriodUsage {
  todayTokens: number;
  weekTokens: number;
  totalTokens: number;
  usedMemberCount: number;
  usedApiKeyCount: number;
}

export interface ProviderChannel {
  id: string;
  name: string;
  slug: string;
  status: "active" | "draining" | "disabled";
  isDefault: boolean;
  routingStrategy: string;
  executionMode: "direct";
  providerInstanceId: string | null;
  providerInstanceName?: string | null;
  providerInstanceStatus?: ProviderInstance["status"] | null;
  providerInstanceActiveRequests?: number | null;
  providerInstanceMaxConcurrency?: number | null;
  allowedModels: string[] | null;
  maxConcurrency: number | null;
  activeRequests: number;
  assignedMemberCount: number;
  assignedApiKeyCount: number;
  tokenUsage: TokenPeriodUsage;
  metadata?: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
}

export interface ChannelOverride {
  id: string;
  subjectType: "member" | "api_key";
  memberId: string;
  apiKeyId: string | null;
  channelId: string;
  channelName?: string | null;
  requestedByMemberId: string;
  reason: string;
  expiresAt: string;
  revokedAt: string | null;
  revokedByMemberId?: string | null;
  revokeReason?: string | null;
  createdAt: string;
}

export interface ProviderInstanceNode {
  id: string;
  name: string;
  accountId: string | null;
  runtimeKey: string;
  mode: "external" | "systemd_instance";
  status: ProviderBridgeInstance["status"];
  activeRequests: number;
  maxConcurrency: number | null;
}

export interface ProviderInstance {
  id: string;
  name: string;
  slug: string;
  status: "active" | "draining" | "disabled";
  routingStrategy: "least_loaded";
  maxConcurrency: number | null;
  activeRequests: number;
  channels: Array<{ id: string; name: string; isDefault: boolean }>;
  nodes: ProviderInstanceNode[];
  tokenUsage: TokenPeriodUsage;
  createdAt: string;
  updatedAt: string;
}

export interface ProviderBridgeInstance {
  id: string;
  providerInstanceId: string;
  providerInstanceName?: string | null;
  channels: Array<{ id: string; name: string; isDefault: boolean }>;
  accountId: string | null;
  accountLabel?: string | null;
  name: string;
  runtimeKey: string;
  mode: "external" | "systemd_instance";
  routingMode: "passthrough" | "instance_alias";
  executionMode: "direct";
  upstreamProtocol: "responses" | "chat_completions";
  host: string;
  port: number | null;
  baseUrl: string;
  upstreamAuthConfigured?: boolean;
  authPath: string;
  serviceName: string;
  status: "configured" | "starting" | "healthy" | "degraded" | "offline" | "disabled";
  maxConcurrency: number | null;
  activeRequests: number;
  cooldownUntil: string | null;
  failureCount: number;
  lastSelectedAt: string | null;
  lastHealthAt: string | null;
  lastError: string | null;
  tokenUsage: TokenPeriodUsage;
}

export interface BridgeFailoverEvent {
  id: string;
  requestId: string | null;
  route: "responses" | "chat";
  memberId: string | null;
  apiKeyId: string | null;
  channelId: string;
  fromInstanceId: string | null;
  toInstanceId: string | null;
  bindingType: string | null;
  bindingKey: string | null;
  trigger: string;
  status: string;
  attempt: number;
  beforeFirstByte: boolean;
  errorStatus: number | null;
  errorCode: string | null;
  errorMessage: string | null;
  createdAt: string;
}

export interface ModelBillingRate {
  model: string;
  modelPattern: string;
  input: number;
  cachedInput: number;
  output: number;
}

export interface ModelBillingRateRow extends ModelBillingRate {
  configured: boolean;
}

export interface BillingModelRates {
  version: string;
  rates: ModelBillingRate[];
  ignoredInternalRateCount?: number;
}

export interface PricingPlan {
  id: string;
  name: string;
  description: string | null;
  price: number;
  faceAmount?: number;
  tokenAmount: number;
  grantPoints?: number;
  productType: "wallet_topup" | "monthly_pass" | string;
  validityMonths: number | null;
  activationWindowDays: number | null;
  currency: string;
  interval: string;
  features: string;
  isFeatured: number;
  isActive: number;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface Order {
  id: string;
  memberId: string;
  planId: string | null;
  status: string;
  amount: number;
  tokenAmount: number;
  productType: "wallet_topup" | "monthly_pass" | string;
  fulfillmentSnapshot?: Record<string, unknown>;
  currency: string;
  paymentMethod: string | null;
  paymentTransactionId: string | null;
  paidAt: string | null;
  fulfilledAt?: string | null;
  refundedAt: string | null;
  refundReason: string | null;
  note: string | null;
  createdAt: string;
  updatedAt: string;
  member?: User | null;
  plan?: PricingPlan | null;
}

export interface CheckoutPayload {
  mode: string;
  channel?: string;
  sdk?: string;
  formAction?: string;
  formMethod?: string;
  fields: Record<string, string>;
  formHtml?: string;
  orderString?: string;
}

export interface PaymentAttempt {
  id: string;
  orderId: string;
  provider: string;
  status: string;
  providerTradeNo: string | null;
  providerBuyerId: string | null;
  providerSubject: string | null;
  checkoutPayload?: CheckoutPayload | null;
  notifyPayload?: Record<string, unknown> | null;
  paidAmount: number | null;
  paidCurrency: string | null;
  paidAt: string | null;
  failedReason: string | null;
  createdAt: string;
  updatedAt: string;
}

// ===== DOM 元素引用类型 =====

export interface Els {
  navItems: NodeListOf<HTMLElement>;
  views: NodeListOf<HTMLElement>;
  pageEyebrow: HTMLElement | null;
  pageTitle: HTMLElement | null;
  pageMeta: HTMLElement | null;
  modalBackdrop: HTMLElement | null;
  modals: NodeListOf<HTMLElement>;
  apiKeyModal: HTMLElement | null;
  createUserModal: HTMLElement | null;
  createUserForm: HTMLFormElement | null;
  rechargeUserModal: HTMLElement | null;
  rechargeUserForm: HTMLFormElement | null;
  policySettingsModal: HTMLElement | null;
  policySettingsForm: HTMLFormElement | null;
  policySettingsTarget: HTMLElement | null;
  billingRateModal: HTMLElement | null;
  billingRateForm: HTMLFormElement | null;
  billingRateTarget: HTMLElement | null;
  pricingPlanModal: HTMLElement | null;
  pricingPlanForm: HTMLFormElement | null;
  pricingPlanModalTitle: HTMLElement | null;
  pricingPlanModalSubmit: HTMLButtonElement | null;
  createModelModal: HTMLElement | null;
  createModelForm: HTMLFormElement | null;
  keyModal: HTMLElement | null;
  apiKeyForm: HTMLFormElement | null;
  apiKeyName: HTMLInputElement | null;
  memberList: HTMLElement | null;
  plainKey: HTMLElement | null;
  refreshLogsButton: HTMLElement | null;
  requestLogList: HTMLElement | null;
  teamOverview: HTMLElement | null;
  todayErrorRequests: HTMLElement | null;
  accountContent: HTMLElement | null;
  userStatus: HTMLElement | null;
  layoutLanding: HTMLElement | null;
  layoutAuth: HTMLElement | null;
  layoutApp: HTMLElement | null;
  layoutAdmin: HTMLElement | null;
  layoutChat: HTMLElement | null;
  landingGuestNav: HTMLElement | null;
  landingGuestChatEntry: HTMLElement | null;
  landingUserBadge: HTMLElement | null;
  landingUserName: HTMLElement | null;
  landingConsoleLink: HTMLButtonElement | null;
  authForm: HTMLFormElement | null;
  authEmail: HTMLInputElement | null;
  authPassword: HTMLInputElement | null;
  authPasswordConfirm: HTMLInputElement | null;
  authPasswordConfirmLabel: HTMLElement | null;
  authName: HTMLInputElement | null;
  authNameLabel: HTMLElement | null;
  authError: HTMLElement | null;
  authSubmitBtn: HTMLButtonElement | null;
  appContent: HTMLElement | null;
  appUserBadge: HTMLElement | null;
  appAdminLink: HTMLElement | null;
  adminUsersContent: HTMLElement | null;
  adminUserDetailContent: HTMLElement | null;
  adminChannelsContent: HTMLElement | null;
  adminWalletsContent: HTMLElement | null;
  adminGptContent: HTMLElement | null;
  adminHealthContent: HTMLElement | null;
  toast: HTMLElement | null;
  refreshButton: HTMLElement | null;
}
